import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Shield, Search, ChevronLeft, ChevronRight, 
  User, Clock, Globe, Monitor, RefreshCw, X,
  LogIn, LogOut, AlertTriangle, UserX, UserCheck, Ban, ShieldAlert, ShieldCheck
} from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';
import { API } from '@/App';
import { toast } from 'sonner';
import { formatDateTimeUS } from '@/utils/formatDate';

// Event type configurations
const EVENT_CONFIG = {
  LOGIN_SUCCESS: { 
    label: 'Login Success', 
    icon: LogIn, 
    color: 'bg-green-100 text-green-700 border-green-200',
    iconColor: 'text-green-600'
  },
  LOGIN_FAIL: { 
    label: 'Login Failed', 
    icon: AlertTriangle, 
    color: 'bg-red-100 text-red-700 border-red-200',
    iconColor: 'text-red-600'
  },
  LOGOUT: { 
    label: 'Logout', 
    icon: LogOut, 
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    iconColor: 'text-blue-600'
  },
  SESSION_EXPIRED: { 
    label: 'Session Expired', 
    icon: Clock, 
    color: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    iconColor: 'text-yellow-600'
  },
  ADMIN_FORCE_LOGOUT: { 
    label: 'Admin Force Logout', 
    icon: UserX, 
    color: 'bg-orange-100 text-orange-700 border-orange-200',
    iconColor: 'text-orange-600'
  },
  ADMIN_FREEZE_USER: { 
    label: 'Account Frozen', 
    icon: Ban, 
    color: 'bg-red-100 text-red-700 border-red-200',
    iconColor: 'text-red-600'
  },
  ADMIN_UNFREEZE_USER: { 
    label: 'Account Unfrozen', 
    icon: UserCheck, 
    color: 'bg-green-100 text-green-700 border-green-200',
    iconColor: 'text-green-600'
  },
  ACCOUNT_SUSPENDED: { 
    label: 'Account Suspended', 
    icon: ShieldAlert, 
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    iconColor: 'text-purple-600'
  },
  ADMIN_UNSUSPEND_USER: { 
    label: 'Account Unsuspended', 
    icon: ShieldCheck, 
    color: 'bg-teal-100 text-teal-700 border-teal-200',
    iconColor: 'text-teal-600'
  }
};

const EventTypeBadge = ({ eventType }) => {
  const config = EVENT_CONFIG[eventType] || { 
    label: eventType, 
    icon: Shield, 
    color: 'bg-gray-100 text-gray-700 border-gray-200',
    iconColor: 'text-gray-600'
  };
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.color}`}>
      <Icon className={`w-3.5 h-3.5 ${config.iconColor}`} />
      {config.label}
    </span>
  );
};

const AuthActivity = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0
  });
  
  // Filters
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    userSearch: '',
    eventType: ''
  });

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('pageSize', pagination.pageSize);
      
      if (filters.fromDate) params.append('from', filters.fromDate);
      if (filters.toDate) params.append('to', filters.toDate);
      if (filters.userSearch) params.append('user', filters.userSearch);
      if (filters.eventType) params.append('type', filters.eventType);
      
      const response = await axios.get(`${API}/admin/auth-events?${params.toString()}`);
      setEvents(response.data.events || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        totalPages: response.data.totalPages
      }));
    } catch (error) {
      console.error('Error loading auth events:', error);
      toast.error('Failed to load auth events');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, filters]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1
  };

  const clearFilters = () => {
    setFilters({
      fromDate: '',
      toDate: '',
      userSearch: '',
      eventType: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const hasActiveFilters = filters.fromDate || filters.toDate || filters.userSearch || filters.eventType;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return formatDateTimeUS(dateStr);
  };

  const truncateUserAgent = (ua) => {
    if (!ua) return '-';
    if (ua.length > 50) return ua.substring(0, 50) + '...';
    return ua;
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="w-7 h-7 text-orange-500" />
              Auth Activity
            </h1>
            <p className="text-gray-600 mt-1">
              View all user login/logout and security events
            </p>
          </div>
          <button
            onClick={loadEvents}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Filters - Always visible in single row */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 p-4">
          <div className="flex flex-wrap items-end gap-4">
            {/* Date From */}
            <div className="flex-1 min-w-[180px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">From Date</label>
              <input
                type="datetime-local"
                value={filters.fromDate}
                onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
              />
            </div>
            
            {/* Date To */}
            <div className="flex-1 min-w-[180px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">To Date</label>
              <input
                type="datetime-local"
                value={filters.toDate}
                onChange={(e) => handleFilterChange('toDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
              />
            </div>
            
            {/* User Search */}
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">Search User</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={filters.userSearch}
                  onChange={(e) => handleFilterChange('userSearch', e.target.value)}
                  placeholder="Search by email..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                />
              </div>
            </div>
            
            {/* Event Type */}
            <div className="flex-1 min-w-[160px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">Event Type</label>
              <select
                value={filters.eventType}
                onChange={(e) => handleFilterChange('eventType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
              >
                <option value="">All Events</option>
                {Object.entries(EVENT_CONFIG).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-sm text-gray-600 hover:text-red-600 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-1"
              >
                <X className="w-4 h-4" />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Events Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Time
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Event Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    IP Address
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Browser
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-4 py-12 text-center">
                      <RefreshCw className="w-8 h-8 text-orange-500 animate-spin mx-auto mb-2" />
                      <p className="text-gray-500">Loading...</p>
                    </td>
                  </tr>
                ) : events.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-4 py-12 text-center">
                      <Shield className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-500">No auth events yet</p>
                    </td>
                  </tr>
                ) : (
                  events.map((event) => (
                    <tr key={event.id} className="hover:bg-gray-50 transition-colors" data-testid={`auth-event-${event.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <Clock className="w-4 h-4 text-gray-400" />
                          {formatDate(event.occurredAt)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {event.userName || event.email || '-'}
                            </div>
                            {event.userName && event.email && (
                              <div className="text-xs text-gray-500">{event.email}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <EventTypeBadge eventType={event.eventType} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Globe className="w-4 h-4 text-gray-400" />
                          {event.ipAddress || '-'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 text-sm text-gray-600" title={event.userAgent}>
                          <Monitor className="w-4 h-4 text-gray-400" />
                          {truncateUserAgent(event.userAgent)}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && pagination.totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between bg-gray-50">
              <div className="text-sm text-gray-600">
                Total <span className="font-medium">{pagination.total}</span> records
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-700">
                  Page <span className="font-medium">{pagination.page}</span> / {pagination.totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AuthActivity;
